import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { jsPDF } from 'jspdf';
import '../styles/QuickSaleForm.css';

const QuickSaleForm = ({ onSaleAdded, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0], // Fecha actual por defecto
    total_amount: '',
    payment_method: 'Efectivo', // Valor por defecto
    point_of_sale_id: '',
    description: ''
  });
  const [printTicket, setPrintTicket] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [loadingPointsOfSale, setLoadingPointsOfSale] = useState(true);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);
  const [businessDateTime, setBusinessDateTime] = useState(null);

  // Función para obtener la fecha y hora del negocio
  const fetchBusinessTime = async (businessId) => {
    if (!businessId) return null;
    
    try {
      console.log('Obteniendo fecha y hora del negocio para el registro de venta...');
      const serverTimeResponse = await axios.get('/api/sales/server-time', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 5000
      });
      
      console.log('Respuesta de fecha/hora del negocio:', serverTimeResponse.data);
      
      // La función get_business_time devuelve la fecha y hora ya ajustada al huso horario del negocio
      const serverTimeStr = serverTimeResponse.data.time;
      
      if (serverTimeStr && typeof serverTimeStr === 'string') {
        // Guardar la fecha/hora completa del negocio
        setBusinessDateTime(serverTimeStr);
        console.log('Fecha/hora completa del negocio para registro en BD:', serverTimeStr);
        return serverTimeStr;
      }
      return null;
    } catch (error) {
      console.error('Error al obtener la fecha/hora del negocio:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPointsOfSale = async () => {
      try {
        setLoadingPointsOfSale(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/points-of-sale/available', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setPointsOfSale(response.data);
        
        // Si solo hay un punto de venta disponible, seleccionarlo automáticamente
        if (response.data.length === 1) {
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: response.data[0].id
          }));
        } else if (response.data.length > 0) {
          // Si hay múltiples puntos de venta, seleccionar el primero por defecto
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: response.data[0].id
          }));
          
          // Obtener la fecha y hora del negocio si tenemos un punto de venta seleccionado
          if (response.data[0].business_id) {
            await fetchBusinessTime(response.data[0].business_id);
          }
        }
      } catch (error) {
        console.error('Error al obtener puntos de venta:', error);
        setError('Error al cargar los puntos de venta. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingPointsOfSale(false);
      }
    };

    fetchPointsOfSale();
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'printTicket') {
        setPrintTicket(checked);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Función para generar el ticket de venta en PDF con la zona horaria del negocio
  const generateTicket = async (saleData) => {
    try {
      console.log('%c Iniciando generación de ticket PDF...', 'background: #222; color: #bada55; font-size: 14px');
      setIsGeneratingTicket(true);
      
      // Obtener la hora del servidor desde el backend con la zona horaria del negocio
      // El backend consulta la función RPC get_business_time que usa businesses.bis_zonahorario
      console.log('INICIO: Obteniendo hora del servidor desde el backend');
      const serverTimeResponse = await axios.get('/api/sales/server-time', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('RESPUESTA COMPLETA del servidor:', serverTimeResponse.data);
      console.log('Hora UTC del servidor:', serverTimeResponse.data.utcNow);
      console.log('Hora ajustada del servidor:', serverTimeResponse.data.time);
      console.log('Zona horaria del negocio:', serverTimeResponse.data.timezone);
      
      // Guardar los datos recibidos del backend
      const serverTimeStr = serverTimeResponse.data.time;
      const businessTimezone = serverTimeResponse.data.timezone || 'America/Mexico_City';
      const utcNow = serverTimeResponse.data.utcNow;
      
      console.log('COMPARACIÓN DE HORAS:');
      console.log('1. Hora UTC actual (frontend):', new Date().toISOString());
      console.log('2. Hora UTC del servidor:', utcNow);
      console.log('3. Hora ajustada del servidor:', serverTimeStr);
      
      // Mostrar la hora local del navegador para comparación
      const browserLocalTime = new Date().toLocaleString();
      console.log('4. Hora local del navegador:', browserLocalTime);
      
      // Variables para almacenar la fecha y hora formateadas
      let formattedDate = '';
      let formattedTime = '';
      
      try {
        // IMPORTANTE: Usar directamente el string de fecha/hora que viene del backend
        // sin crear un nuevo objeto Date para evitar conversiones automáticas
        if (typeof serverTimeStr === 'string') {
          // Intentar extraer la fecha y hora directamente del string
          // Formato esperado: "2025-06-05T16:54:12-06:00" o similar
          const dateTimeParts = serverTimeStr.split('T');
          if (dateTimeParts.length === 2) {
            const datePart = dateTimeParts[0]; // "2025-06-05"
            let timePart = dateTimeParts[1]; // "16:54:12-06:00"
            
            // Extraer solo la hora sin la zona horaria
            timePart = timePart.split(/[+-]|Z/)[0]; // "16:54:12"
            
            console.log('Fecha extraída directamente:', datePart);
            console.log('Hora extraída directamente:', timePart);
            
            // Formatear la fecha manualmente
            const [year, month, day] = datePart.split('-');
            formattedDate = `${day}/${month}/${year}`;
            
            // Simplificar el formato de hora para mostrar solo HH:MM:SS
            // Primero dividimos por el punto para eliminar milisegundos si existen
            const timeWithoutMillis = timePart.split('.')[0];
            
            // La hora ya viene en formato 24h, solo necesitamos asegurarnos de que tenga segundos
            formattedTime = timeWithoutMillis;
            if (timeWithoutMillis.split(':').length === 2) {
              formattedTime = `${timeWithoutMillis}:00`; // Agregar segundos si no los tiene
            }
            
            console.log('Fecha formateada manualmente:', formattedDate);
            console.log('Hora formateada manualmente:', formattedTime);
          } else {
            throw new Error('Formato de fecha inesperado');
          }
        } else {
          throw new Error('La hora del servidor no es un string');
        }
      } catch (error) {
        console.error('ERROR al procesar la fecha/hora:', error);
        
        // Plan B: Intentar usar el objeto Date como respaldo
        try {
          console.log('Intentando plan B con objeto Date...');
          const serverTime = new Date(serverTimeStr);
          console.log('Objeto Date creado:', serverTime.toString());
          
          // Formatear usando Intl.DateTimeFormat para mayor control
          const dateFormatter = new Intl.DateTimeFormat('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: businessTimezone
          });
          
          const timeFormatter = new Intl.DateTimeFormat('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: businessTimezone
          });
          
          formattedDate = dateFormatter.format(serverTime);
          formattedTime = timeFormatter.format(serverTime);
          
          console.log('Fecha formateada con Intl:', formattedDate);
          console.log('Hora formateada con Intl:', formattedTime);
        } catch (backupError) {
          console.error('ERROR en plan B:', backupError);
          // Último recurso: usar la hora local
          const now = new Date();
          formattedDate = now.toLocaleDateString('es-MX');
          formattedTime = now.toLocaleTimeString('es-MX', { hour12: false });
          console.log('Usando hora local como último recurso');
        }
      }
      
      console.log('RESULTADO FINAL:');
      console.log('Fecha que se mostrará en el ticket:', formattedDate);
      console.log('Hora que se mostrará en el ticket:', formattedTime);
      
      // Obtener datos del negocio y punto de venta
      console.log('%c Obteniendo datos del negocio...', 'background: #222; color: #bada55');
      const businessResponse = await axios.get('/api/businesses/me', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('%c Datos del negocio recibidos:', 'background: #222; color: #bada55', businessResponse.data);
      
      const posResponse = await axios.get(`/api/points-of-sale/${saleData.pointOfSaleId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('%c Datos del punto de venta recibidos:', 'background: #222; color: #bada55', posResponse.data);
      
      const business = businessResponse.data;
      const pointOfSale = posResponse.data;
      
      // Obtener datos del usuario
      const userResponse = await axios.get('/api/users/me', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const user = userResponse.data;
      
      // Crear un ID único para la venta usando el nombre del usuario (truncado a 12 caracteres)
      const truncatedName = user.full_name ? user.full_name.substring(0, 12).replace(/\\s+/g, '_') : 'user';
      const saleId = `${truncatedName}_${Date.now()}`;
      
      // Crear el documento PDF con ancho de 58mm (ticket pequeño)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [58, 200] // Ancho de 58mm, altura ajustable
      });
      
      doc.setFont('helvetica');
      doc.setFontSize(10);
      
      // Encabezado del ticket
      doc.setFontSize(10);
      doc.text(business.bis_name || 'Negocio', 29, 10, null, null, 'center');
      doc.text('Ticket de Venta', 29, 15, null, null, 'center');
      doc.text(`Tel: ${business.bis_phone || 'No disponible'}`, 29, 20, null, null, 'center');
      doc.text('Venta Rápida', 29, 25, null, null, 'center');
      
      // Información de la venta
      // Priorizar el nombre del punto de venta del objeto saleData que viene directamente del formulario
      // Este es el valor más confiable ya que se obtiene directamente de la lista seleccionable
      console.log('%c saleData completo:', 'background: #222; color: #bada55', saleData);
      console.log('%c pointOfSale completo:', 'background: #222; color: #bada55', pointOfSale);
      
      // Usar pos_name en lugar de pos_nombre para mantener consistencia
      const displayPosName = saleData.pointOfSaleName || (pointOfSale && pointOfSale.pos_name ? pointOfSale.pos_name : 'No disponible');
      console.log('%c Nombre del punto de venta a mostrar en ticket:', 'background: #222; color: #bada55', displayPosName);
      
      // Verificar si el nombre está definido y no está vacío
      if (!displayPosName || displayPosName === 'No disponible') {
        console.error('%c ALERTA: Nombre del punto de venta no disponible', 'background: red; color: white');
      }
      
      doc.text(`Punto de venta: ${displayPosName}`, 2, 35);
      // Truncar el nombre del usuario a 12 caracteres
      const userName = user && user.full_name ? user.full_name.substring(0, 12) : 'No disponible';
      doc.text(`Vendedor: ${userName}`, 2, 40);
      doc.text(`Fecha: ${formattedDate || 'No disponible'}`, 2, 45);
      doc.text(`Hora: ${formattedTime || 'No disponible'} (${businessTimezone || 'UTC'})`, 2, 50);
      // Línea de Folio en blanco como solicitado
      // doc.text(`Folio: `, 2, 55);
      
      // Línea divisoria
      doc.line(2, 60, 56, 60);
      doc.setFontSize(9);
      doc.text('Detalle de la venta', 29, 65, null, null, 'center');
      doc.setFontSize(8);
      doc.text('Producto', 2, 70);
      doc.text('Total', 52, 70, null, null, 'right');
      
      // Línea divisoria
      doc.line(2, 75, 56, 75);
      
      // Productos
      let y = 80;
      if (saleData.products && saleData.products.length > 0) {
        saleData.products.forEach(product => {
          const productName = product.name || 'Producto sin nombre';
          const total = product.total || 0;
          
          // Limitar el nombre del producto a un máximo de caracteres
          const maxLength = 20; // Reducido para ticket pequeño
          const displayName = productName.length > maxLength ? 
            productName.substring(0, maxLength) + '...' : 
            productName;
          
          doc.text(displayName, 2, y);
          doc.text(`$${total.toFixed(2)}`, 52, y, null, null, 'right');
          
          y += 5;
        });
      } else {
        // Para venta rápida, mostrar una línea genérica
        doc.text('Venta rápida', 2, y);
        doc.text(`$${saleData.total.toFixed(2)}`, 52, y, null, null, 'right');
        y += 5;
      }
      
      // Línea divisoria
      doc.line(2, y, 56, y);
      y += 5;
      
      // Totales
      const subtotal = saleData.total; // En venta rápida, el subtotal es igual al total
      
      doc.text(`Subtotal: $${subtotal.toFixed(2)}`, 52, y, null, null, 'right');
      y += 5;
      // Línea de IVA en blanco como solicitado
      // doc.text(`IVA (16%): $0.00`, 52, y, null, null, 'right');
      y += 5;
      doc.setFontSize(10);
      doc.text(`Total: $${saleData.total.toFixed(2)}`, 52, y, null, null, 'right');
      doc.setFontSize(8);
      y += 10;
      
      // Método de pago
      doc.text(`Método de pago: ${saleData.paymentMethod || 'Efectivo'}`, 2, y);
      y += 5;
      
      if (saleData.paymentMethod === 'Efectivo' && saleData.cashReceived && saleData.change) {
        doc.text(`Recibido: $${saleData.cashReceived.toFixed(2)}`, 2, y);
        y += 5;
        doc.text(`Cambio: $${saleData.change.toFixed(2)}`, 2, y);
        y += 5;
      }
      
      // Pie de página
      y += 10;
      doc.text('Gracias por su compra', 29, y, null, null, 'center');
      y += 5;
      
      // Crear ID de venta único
      const dateTimeStr = formattedDate.replace(/\//g, '') + formattedTime.replace(/:/g, '');
      // Asegurarse de que pointOfSale y pos_nombre existan antes de intentar usar replace
      let idPosName = 'POS';
      if (pointOfSale && pointOfSale.pos_nombre) {
        idPosName = pointOfSale.pos_nombre.replace(/\s+/g, '');
      } else if (saleData.pointOfSaleName) {
        // Usar el nombre del punto de venta directamente del formulario si está disponible
        idPosName = saleData.pointOfSaleName.replace(/\s+/g, '');
      }
      const userNameStr = userName.replace(/\s+/g, '');
      const totalInt = Math.floor(saleData.total);
      const saleIdUnique = `${dateTimeStr}${idPosName}${userNameStr}${totalInt}`;
      
      doc.text('IdVenta:', 2, y);
      y += 5;
      
      // Dividir el ID de venta en múltiples líneas de 37 caracteres cada una
      if (saleIdUnique.length > 37) {
        // Calcular cuántas líneas necesitamos
        const numLines = Math.ceil(saleIdUnique.length / 37);
        
        // Dividir el string en partes de 37 caracteres y mostrar cada parte en una línea
        for (let i = 0; i < numLines; i++) {
          const startIndex = i * 37;
          const endIndex = Math.min(startIndex + 37, saleIdUnique.length);
          const linePart = saleIdUnique.substring(startIndex, endIndex);
          
          doc.text(linePart, 2, y);
          
          // Incrementar Y solo si no es la última línea
          if (i < numLines - 1) {
            y += 5;
          }
        }
      } else {
        doc.text(saleIdUnique, 2, y);
      }
      
      // Generar el nombre del archivo con la fecha y hora
      const dateForFilename = formattedDate.replace(/\//g, '-');
      const timeForFilename = formattedTime.replace(/:/g, '-').replace(/\s/g, '');
      const filename = `ticket_venta_${dateForFilename}_${timeForFilename}.pdf`;
      
      console.log('%c Intentando descargar el PDF...', 'background: #222; color: #bada55; font-size: 14px');
      
      // Guardar el PDF usando el método directo primero
      try {
        console.log('%c Intentando método 1: doc.save()', 'background: #222; color: #bada55');
        doc.save(filename);
        console.log('%c Método 1 completado sin errores', 'background: #222; color: #bada55');
      } catch (saveError) {
        console.error('Error en método 1:', saveError);
        
        // Si falla, intentar con el método alternativo
        try {
          console.log('%c Intentando método 2: blob y createObjectURL', 'background: #222; color: #bada55');
          const blob = doc.output('blob');
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link); // Agregar al DOM para mayor compatibilidad
          console.log('%c Link creado:', 'background: #222; color: #bada55', link);
          
          // Simular clic con un pequeño retraso
          setTimeout(() => {
            console.log('%c Ejecutando clic en el link', 'background: #222; color: #bada55');
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            console.log('%c Método 2 completado', 'background: #222; color: #bada55');
          }, 100);
        } catch (blobError) {
          console.error('Error en método 2:', blobError);
          
          // Último intento: usar window.open con data URI
          try {
            console.log('%c Intentando método 3: window.open con data URI', 'background: #222; color: #bada55');
            const dataUri = doc.output('datauristring');
            window.open(dataUri);
            console.log('%c Método 3 completado', 'background: #222; color: #bada55');
          } catch (dataUriError) {
            console.error('Error en método 3:', dataUriError);
            throw new Error('No se pudo descargar el PDF después de intentar 3 métodos diferentes');
          }
        }
      }
      
      setIsGeneratingTicket(false);
      return true;
    } catch (error) {
      console.error('Error al generar el ticket:', error);
      setIsGeneratingTicket(false);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }

      // Validar campos requeridos
      if (!formData.sale_date || !formData.total_amount || !formData.point_of_sale_id || !formData.payment_method) {
        setError('Todos los campos son obligatorios');
        setLoading(false);
        return;
      }

      // Validar que el monto total sea un número positivo
      if (isNaN(formData.total_amount) || parseFloat(formData.total_amount) <= 0) {
        setError('El monto total debe ser un número positivo');
        setLoading(false);
        return;
      }

      // Actualizar la fecha y hora del negocio antes de enviar
      let currentBusinessTime = businessDateTime;
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
      
      if (selectedPointOfSale?.business_id) {
        const freshBusinessTime = await fetchBusinessTime(selectedPointOfSale.business_id);
        if (freshBusinessTime) {
          currentBusinessTime = freshBusinessTime;
        }
      }
      
      // Combinar la fecha seleccionada por el usuario con la zona horaria del negocio
    let finalSaleDate = formData.sale_date;
    
    try {
      // Si tenemos la fecha/hora del negocio y el usuario ha seleccionado una fecha
      if (currentBusinessTime && formData.sale_date) {
        console.log('Fecha seleccionada por el usuario (original):', formData.sale_date);
        
        // Extraer componentes de la fecha seleccionada por el usuario
        // Formato esperado: YYYY-MM-DD
        const [userYear, userMonth, userDay] = formData.sale_date.split('-').map(Number);
        
        // Crear objeto Date con la fecha/hora del negocio
        const businessDate = new Date(currentBusinessTime);
        console.log('Fecha/hora del negocio (original):', businessDate.toISOString());
        
        // Crear una nueva fecha combinando la fecha del usuario con la hora del negocio
        // IMPORTANTE: Usamos UTC para evitar problemas de zona horaria
        const combinedDate = new Date(Date.UTC(
          userYear,
          userMonth - 1, // Meses en JavaScript son 0-indexed
          userDay,
          businessDate.getUTCHours(),
          businessDate.getUTCMinutes(),
          businessDate.getUTCSeconds(),
          businessDate.getUTCMilliseconds()
        ));
        
        // Convertir a string ISO
        finalSaleDate = combinedDate.toISOString();
        console.log('Fecha combinada (UTC, fecha del usuario + hora del negocio):', finalSaleDate);
      } else {
        // Si no tenemos fecha/hora del negocio, usar la fecha seleccionada por el usuario
        console.log('Usando fecha seleccionada por el usuario:', formData.sale_date);
      }
    } catch (dateError) {
      console.error('Error al combinar fecha del usuario con zona horaria del negocio:', dateError);
      // En caso de error, usar la fecha seleccionada por el usuario como respaldo
    }
      
      console.log('Fecha final a registrar:', finalSaleDate);
      
      // Enviar datos al servidor con la fecha combinada (fecha del usuario + zona horaria del negocio)
      const response = await axios.post('/api/sales/quick', 
        {
          sale_date: finalSaleDate, // Usar la fecha seleccionada por el usuario con la zona horaria del negocio
          total_amount: parseFloat(formData.total_amount),
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          description: formData.description && formData.description.trim() !== '' ? formData.description.trim() : null
        }, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Si está marcada la opción de imprimir ticket y la venta se registró correctamente
      // Preparamos los datos pero NO generamos el ticket aquí para evitar duplicación
      // La generación se hará más abajo con setTimeout para asegurar que la UI se actualice primero
      if (printTicket && response.data) {
        console.log('Opción de imprimir ticket marcada, se generará PDF después de actualizar la UI...');
      }

      // Limpiar el formulario
      setFormData({
        sale_date: new Date().toISOString().split('T')[0],
        total_amount: '',
        payment_method: 'Efectivo', // Mantener el valor por defecto
        point_of_sale_id: formData.point_of_sale_id, // Mantener el punto de venta seleccionado
        description: ''
      });
      
      // Mantener la preferencia de impresión de ticket
      // setPrintTicket(false); // Descomenta esta línea si quieres que se desmarque después de cada venta

      console.log('%c Venta registrada exitosamente', 'background: green; color: white; font-size: 14px');
      
      // Si la casilla de imprimir ticket está marcada
      if (printTicket) {
        console.log('%c Casilla de imprimir ticket marcada', 'background: #222; color: #bada55; font-size: 14px');
        
        // Mostrar mensaje de éxito pero permanecer en la página
        setError(null);
        setSuccessMessage('Venta registrada. Generando ticket...');
        
        // Generar el ticket con un pequeño retraso para asegurar que la UI se actualice
        setTimeout(async () => {
          try {
            console.log('%c Generando ticket desde handleSubmit', 'background: #222; color: #bada55; font-size: 14px');
            const saleId = response.data.id || 'N/A';
            // Obtener el nombre del punto de venta seleccionado
            console.log('%c Puntos de venta disponibles:', 'background: #222; color: #bada55', pointsOfSale);
            console.log('%c ID del punto de venta seleccionado:', 'background: #222; color: #bada55', formData.point_of_sale_id);
            
            const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
            console.log('%c Punto de venta seleccionado:', 'background: #222; color: #bada55', selectedPointOfSale);
            
            // Usar pos_name en lugar de pos_nombre para mantener consistencia
            const pointOfSaleName = selectedPointOfSale ? selectedPointOfSale.pos_name : 'No disponible';
            console.log('%c Nombre del punto de venta a usar:', 'background: #222; color: #bada55', pointOfSaleName);
            
            const saleData = {
              pointOfSaleId: formData.point_of_sale_id,
              pointOfSaleName: pointOfSaleName, // Agregar el nombre del punto de venta
              paymentMethod: formData.payment_method,
              total: parseFloat(formData.total_amount),
              products: [], // Venta rápida sin productos detallados
              saleId: saleId // Pasar el ID de la venta
            };
            
            await generateTicket(saleData);
            
            // Notificar al componente padre después de un tiempo para dar tiempo a la descarga
            setTimeout(() => {
              console.log('%c Notificando al componente padre', 'background: #222; color: #bada55');
              onSaleAdded();
            }, 2000);
          } catch (ticketError) {
            console.error('Error al generar ticket desde setTimeout:', ticketError);
            setError('Error al generar el ticket: ' + ticketError.message);
          }
        }, 500);
      } else {
        // Si no está marcada la casilla, simplemente notificar al componente padre
        console.log('%c Casilla de imprimir ticket NO marcada', 'background: #222; color: #bada55');
        onSaleAdded();
      }
    } catch (error) {
      console.error('Error al registrar la venta:', error);
      setError(error.response?.data?.message || 'Error al registrar la venta. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quick-sale-form-container">
      <h3>Venta Rápida</h3>
      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      <form className="quick-sale-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="point_of_sale_id">Punto de Venta *</label>
          <select
            id="point_of_sale_id"
            name="point_of_sale_id"
            value={formData.point_of_sale_id}
            onChange={handleChange}
            required
            disabled={loadingPointsOfSale || pointsOfSale.length <= 1}
          >
            <option value="">Seleccione un punto de venta</option>
            {pointsOfSale.map(pos => (
              <option key={pos.id} value={pos.id}>
                {pos.pos_name}
              </option>
            ))}
          </select>
          {loadingPointsOfSale && <div className="loading-indicator">Cargando puntos de venta...</div>}
        </div>
        <div className="form-group">
          <label htmlFor="sale_date">Fecha de Venta *</label>
          <input
            type="date"
            id="sale_date"
            name="sale_date"
            value={formData.sale_date}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="total_amount">Monto Total *</label>
          <div className="input-with-icon">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              id="total_amount"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              placeholder="0.00"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">Detalle de la Venta (opcional)</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            placeholder="Registra los productos o servicios separados por coma ej: GloboMetalico, CuadernoDeRayas, BlusaBlancaS, VelaBautizo"
          />
        </div>
        <div className="form-group">
          <label htmlFor="payment_method">Método de Pago *</label>
          <div className="payment-method-options">
            <div className={`payment-option ${formData.payment_method === 'Efectivo' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_cash"
                name="payment_method"
                value="Efectivo"
                checked={formData.payment_method === 'Efectivo'}
                onChange={handleChange}
                required
              />
              <label htmlFor="payment_cash">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <circle cx="12" cy="12" r="2" />
                  <path d="M6 12h.01M18 12h.01" />
                </svg>
                Efectivo
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Tarjeta' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_card"
                name="payment_method"
                value="Tarjeta"
                checked={formData.payment_method === 'Tarjeta'}
                onChange={handleChange}
              />
              <label htmlFor="payment_card">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Tarjeta
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Transferencia' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_transfer"
                name="payment_method"
                value="Transferencia"
                checked={formData.payment_method === 'Transferencia'}
                onChange={handleChange}
              />
              <label htmlFor="payment_transfer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  <polyline points="16 3 12 7 8 3" />
                </svg>
                Transferencia
              </label>
            </div>
          </div>
        </div>
        <div className="form-group print-ticket-option">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="printTicket"
              checked={printTicket}
              onChange={handleChange}
            />
            <span className="checkbox-label">Generar Ticket de Venta (58mm)</span>
          </label>
        </div>
        <div className="form-buttons">
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading || isGeneratingTicket}>
            Cancelar
          </button>
          <button type="submit" className="submit-btn" disabled={loading || isGeneratingTicket}>
            {loading || isGeneratingTicket ? 'Procesando...' : 'Registrar Venta'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuickSaleForm;
